import { createContext, type FC, type ReactNode, useContext } from "react";
import { Navigate } from "react-router-dom";
import { LoadingFallback } from "../components/LoadingFallback/LoadingFallback";
import { useOpenFeatureFlags } from "../hooks/useOpenFeatureFlag";
import { trackErrorEvent } from "../utils/analytics";
import { useChosenDepartment } from "./ChosenDepartment";

export const FEATURE_FLAGS = {
	Annie: "annie",
	TeamTab: "team_tab",
	ManagementCenterTab: "management_center_tab",
	ShiftInsightsTab: "shift_insights_tab",
	MasterScheduleTab: "master_schedule_tab",
	CoveragePeopleTabs: "coverage_people_tabs",
	StaffingPeopleTab: "staffing_people_tab",
	PlanningTab: "planning_tab",
	UserGuideChat: "user_guide_chat",
	StaffingTab: "staffing_tab",
	CommandCenterTab: "command_center_tab",
	EnableSwap: "enable_swap",
	EnableShiftBonuses: "enable_shift_bonuses",
	BlockRecommendationsV2: "block_recommendations_v2",
	RecurringPanelV2Enabled: "recurring_panel_v2_enabled",
	DraftModeEnabled: "draft_mode_enabled",
	HasRecurringShift: "has_recurring_shift",
	FacilitiesTab: "facilities_tab",
	BedSideCallOff: "bed_side_call_off",
	SurfaceDisciplinaryCallOffImpact: "surface_disciplinary_call_off_impact",
	PbjCalculatorTab: "pbj_calculator_tab",
	PbjModel: "pbj_model",
	SelfSchedulingEnabled: "self_scheduling_enabled",
	ShouldDisplayLocationInStaffApp: "should_display_location_in_staff_app",
	TimeOffRequestsEnabled: "time_off_requests_enabled",
	CallOffAgent: "call_off_agent",
	ReplacementRequestEnabled: "replacement_request_enabled",
	BedSideNotes: "bed_side_notes",
	SaveAsDefaultEnabled: "save_as_default_enabled",
	ShowMissingBreaksBadge: "show_missing_breaks_badge",
	PartialPickup: "partial_pickup",
	LongShiftsBadge: "long_shifts_badge",
	JobManagementEnabled: "job_management_enabled",
	DelayedAgencyPostingEnabled: "delayed_agency_posting_enabled",
	CancelAgencyEnabled: "cancel_agency_enabled",
	// DAT-885 — gates the agency activate/deactivate toggle in Department Configuration. It gates the
	NameFormatLastFirst: "name_format_last_first",
	ConsecutiveRecurringShifts: "consecutive_recurring_shifts",
	ConsecutiveMasterSchedule: "consecutive_master_schedule",
	PickupByLocation: "pickup_by_location",
	SmsPickup: "sms_pickup",
	InAppRatingPrompt: "in_app_rating_prompt",
	FutureLookingWeeklyOvertime: "future-looking-weekly-overtime",
	MasterScheduleOverstaffingHighlight: "master_schedule_overstaffing_highlight",
	ScheduleTemplateRuntimeWeekStart: "schedule_template_runtime_week_start",
	HiringModule: "hiring_module",
	HiringIhh: "hiring_ihh",
	StaffingDynamicWeekStart: "staffing_dynamic_week_start",
	StaffingScheduleCache: "staffing_schedule_cache",
	AutoApproveShifts: "auto_approve_shifts",
	MobileGapsView: "mobile_gaps_view",
} as const satisfies Record<string, string>;

export type FeatureFlag = (typeof FEATURE_FLAGS)[keyof typeof FEATURE_FLAGS];

export const ALL_FEATURE_FLAGS: readonly FeatureFlag[] = Object.values(FEATURE_FLAGS);

interface FeatureFlagsContextType {
	flags: Record<string, boolean>;
	isLoading: boolean;
	error: Error | null;
	refetch: () => void;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextType | undefined>(undefined);

interface FeatureFlagsProviderProps {
	children: ReactNode;
}

/**
 * Provider that fetches all feature flags in a single batch API call.
 *
 * Implementation:
 * - Backend: LaunchDarkly SDK with streaming (real-time updates)
 * - Frontend: Fetches on mount, page refresh, and department switch
 * - Components read from context (React Query cache - Layer 1)
 */
export const FeatureFlagsProvider: FC<FeatureFlagsProviderProps> = ({ children }) => {
	const { chosenDepartment } = useChosenDepartment();

	const {
		data: flags = {},
		isLoading,
		error,
		refetch,
	} = useOpenFeatureFlags([...ALL_FEATURE_FLAGS], chosenDepartment.department_id, {
		staleTime: 0,
		enabled: !!chosenDepartment.department_id,
	});

	// If error AND no data (both LaunchDarkly and cache failed), redirect to 404
	if (error && !isLoading) {
		trackErrorEvent("feature flags failed - both launchDarkly and cache are unavailable, redirecting to 404", error, {});
		return <Navigate to="/404" replace />;
	}

	if (isLoading) {
		return <LoadingFallback />;
	}

	return <FeatureFlagsContext.Provider value={{ flags, isLoading, error, refetch }}>{children}</FeatureFlagsContext.Provider>;
};

export const useFeatureFlagsContext = (): FeatureFlagsContextType => {
	const context = useContext(FeatureFlagsContext);
	if (!context) {
		throw new Error("useFeatureFlagsContext must be used within a FeatureFlagsProvider");
	}
	return context;
};
